import type { Grain } from "../api";
import { formatMonthLabel } from "./monthLabel";
import { formatWeekRange } from "./weekLabel";

/**
 * `search.grain`, defaulting to `fallback` — the only two valid values
 * (cvce-pipeline-4). The Pipeline tab's own default is "week"; the Accuracy
 * findings tab keeps its pre-existing default of "month" (spec: "Month is
 * its current behaviour") when no `grain` is in the URL at all.
 */
export function readGrain(search: Record<string, unknown>, fallback: Grain = "week"): Grain {
  if (search.grain === "month") return "month";
  if (search.grain === "week") return "week";
  return fallback;
}

/**
 * `search.period`, with the old `?week=` still read as the week grain's
 * period (cvce-pipeline-4 Acceptance 2: "the old `?week=` still opens the
 * week view"). Only consulted when `grain` is "week" — a stray `week=`
 * alongside `grain=month` is not a month period and is ignored.
 */
export function readPeriod(search: Record<string, unknown>, grain: Grain): string | null {
  if (typeof search.period === "string") return search.period;
  if (grain === "week" && typeof search.week === "string") return search.week;
  if (grain === "month" && typeof search.month === "string") return search.month;
  return null;
}

/** "21-27 Sep" for a week period, "September 2026" for a month period, each with "(in progress)" when incomplete. */
export function formatPeriodLabel(grain: Grain, periodStart: string, complete = true): string {
  const label = grain === "month" ? formatMonthLabel(periodStart) : formatWeekRange(periodStart);
  return complete ? label : `${label} (in progress)`;
}

/** "week"/"month" — the noun the copy uses ("Nothing needs fixing this week/month."). */
export function periodNoun(grain: Grain): "week" | "month" {
  return grain;
}
